import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Send, Loader2 } from 'lucide-react';
import { sendContactEmail } from '../services/contactService';
import Button from './ui/Button';
import Input from './ui/Input';

const ContactForm = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await sendContactEmail({
        subject: formData?.subject,
        message: formData?.message,
        userEmail: formData?.userEmail
      });
      
      setSubmitStatus({ type: 'success', message: 'Your message has been sent successfully!' });
      reset();
      
      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error?.message || 'Failed to send message. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <p className="text-gray-600">
            Have a question or need to report a problem? Fill out the form below and we'll get back to you as soon as possible.
          </p>

          {/* Email Field */}
          <div>
            <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-2">
              Your Email Address *
            </label>
            <Input
              id="userEmail"
              type="email"
              placeholder="your.email@example.com"
              {...register('userEmail', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className={errors?.userEmail ? 'border-red-500' : ''}
            />
            {errors?.userEmail && (
              <p className="mt-1 text-sm text-red-600">{errors?.userEmail?.message}</p>
            )}
          </div>

          {/* Subject Field */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
            </label>
            <Input
              id="subject"
              type="text"
              placeholder="Brief description of your question or issue"
              {...register('subject', {
                required: 'Subject is required',
                minLength: {
                  value: 3,
                  message: 'Subject must be at least 3 characters'
                }
              })}
              className={errors?.subject ? 'border-red-500' : ''}
            />
            {errors?.subject && (
              <p className="mt-1 text-sm text-red-600">{errors?.subject?.message}</p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              id="message"
              rows="6"
              placeholder="Please provide details about your question or the problem you're experiencing..."
              {...register('message', {
                required: 'Message is required',
                minLength: {
                  value: 10,
                  message: 'Message must be at least 10 characters'
                }
              })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors?.message ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors?.message && (
              <p className="mt-1 text-sm text-red-600">{errors?.message?.message}</p>
            )}
          </div>

          {/* Status Message */}
          {submitStatus && (
            <div
              className={`p-4 rounded-md ${
                submitStatus?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {submitStatus?.message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;